import { useMemo, useState } from 'react';
import { isoDate, resolveCalendar, type Calendar, type CalendarException, type WeekDays } from '@bigint-project/core';
import { WeekDayEditor } from './WeekDayEditor.js';
import { ExceptionListEditor } from './ExceptionListEditor.js';
import { CalendarMonthPreview } from './CalendarMonthPreview.js';
import { findCoveringException } from './exception-coverage.js';
import { minutesToHHmm } from './time-format.js';
import type { CalendarDayInfo, CalendarDayStatus } from './types.js';

export interface CalendarEditorProps {
  readonly readOnly?: boolean;
  readonly calendar: Calendar;
  /** 供繼承鏈解析與月曆預覽使用的所有日曆（含自身）。 */
  readonly allCalendars: ReadonlyMap<string, Calendar>;
  readonly onWeekDaysChange: (weekDays: WeekDays) => void;
  readonly onExceptionsChange: (exceptions: readonly CalendarException[]) => void;
  /** 月曆預覽初始顯示的年月；未提供時預設為今日所在月份。主要供測試指定固定月份。 */
  readonly initialPreviewYear?: number;
  readonly initialPreviewMonth?: number;
}

function collectExceptionChain(calendar: Calendar, allCalendars: ReadonlyMap<string, Calendar>): CalendarException[] {
  const chain: CalendarException[] = [];
  const seen = new Set<string>();
  let current: Calendar | undefined = calendar;
  while (current !== undefined && !seen.has(current.id as string)) {
    seen.add(current.id as string);
    chain.push(...current.exceptions);
    current = current.baseCalendarId === null ? undefined : allCalendars.get(current.baseCalendarId as string);
  }
  return chain;
}

function resolveBaseWeekDays(calendar: Calendar, allCalendars: ReadonlyMap<string, Calendar>): WeekDays | null {
  if (calendar.baseCalendarId === null) return null;
  const base = allCalendars.get(calendar.baseCalendarId as string);
  return base?.weekDays ?? null;
}

function pad2(n: number): string {
  return String(n).padStart(2, '0');
}

function daysInMonth(year: number, month: number): number {
  return new Date(year, month, 0).getDate();
}

/**
 * 單一日曆的完整編輯畫面：每週工作時間、例外日、月曆預覽三合一。
 * 純展示元件：props in / callbacks out。日曆解析運算全部委派給 core 的 resolveCalendar，
 * 元件本身不重新實作排程語義。
 */
export function CalendarEditor({
  readOnly = false,
  calendar,
  allCalendars,
  onWeekDaysChange,
  onExceptionsChange,
  initialPreviewYear,
  initialPreviewMonth,
}: CalendarEditorProps): React.JSX.Element {
  const today = new Date();
  const [previewYear, setPreviewYear] = useState(initialPreviewYear ?? today.getFullYear());
  const [previewMonth, setPreviewMonth] = useState(initialPreviewMonth ?? today.getMonth() + 1);

  const baseWeekDays = resolveBaseWeekDays(calendar, allCalendars);

  const resolved = useMemo(() => resolveCalendar(calendar, allCalendars), [calendar, allCalendars]);
  const exceptionChain = useMemo(() => collectExceptionChain(calendar, allCalendars), [calendar, allCalendars]);

  const days: CalendarDayInfo[] = useMemo(() => {
    const count = daysInMonth(previewYear, previewMonth);
    const result: CalendarDayInfo[] = [];
    for (let d = 1; d <= count; d += 1) {
      const isoText = `${previewYear}-${pad2(previewMonth)}-${pad2(d)}`;
      const iso = isoDate(isoText);
      const isWorking = resolved.isWorkingDay(iso);
      const segments = resolved.workingTimesOn(iso);
      const matchedException = findCoveringException(exceptionChain, isoText);
      let status: CalendarDayStatus;
      if (matchedException !== null) {
        status = isWorking ? 'exception-working' : 'exception-non-working';
      } else {
        status = isWorking ? 'working' : 'non-working';
      }
      result.push({
        date: isoText,
        status,
        exceptionName: matchedException?.name ?? null,
        timeRanges: segments.map((s) => `${minutesToHHmm(s.start as number)}–${minutesToHHmm(s.end as number)}`),
      });
    }
    return result;
  }, [previewYear, previewMonth, resolved, exceptionChain]);

  const goPrevMonth = (): void => {
    if (previewMonth === 1) {
      setPreviewMonth(12);
      setPreviewYear((y) => y - 1);
    } else {
      setPreviewMonth((m) => m - 1);
    }
  };
  const goNextMonth = (): void => {
    if (previewMonth === 12) {
      setPreviewMonth(1);
      setPreviewYear((y) => y + 1);
    } else {
      setPreviewMonth((m) => m + 1);
    }
  };

  return (
    <div data-testid="calendar-editor" className="space-y-4">
      <div>
        <div className="mb-1.5 font-medium text-fg">每週工作時間</div>
        <fieldset disabled={readOnly} className="border-0 p-0">
          <WeekDayEditor weekDays={calendar.weekDays} baseWeekDays={baseWeekDays} onChange={onWeekDaysChange} />
        </fieldset>
      </div>

      <div>
        <div className="mb-1.5 font-medium text-fg">例外日</div>
        <fieldset disabled={readOnly} className="border-0 p-0">
          <ExceptionListEditor exceptions={calendar.exceptions} onChange={onExceptionsChange} />
        </fieldset>
      </div>

      <div>
        <div className="mb-1.5 font-medium text-fg">月曆預覽</div>
        <CalendarMonthPreview
          year={previewYear}
          month={previewMonth}
          days={days}
          onPrevMonth={goPrevMonth}
          onNextMonth={goNextMonth}
        />
      </div>
    </div>
  );
}
